import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _isEmpty from "lodash/isEmpty";
import _isNil from "lodash/isNil";
import { getAPRIntroMessageList, getAnnualFeeDetails, getBalanceTransferFee, getBalanceTransferFeeDescription, getOngoingAPR, getMaximumFICO, getMinimumDeposit, getMinimumDepositDescription, getMinimumFICO, getRewardCentsPerUnitValue, getRewardRateDetails, getRewardRateNotes, getSignUpBonus, getSignUpBonusDetails, getSignUpBonusType } from '../selectors/credit-cards';
import { getIntroOfferValue, getIntroOfferValueTooltip, getRecommendedCreditScoreFormattedDetails, getRewardRateDescription, isDiscover } from '../utils/credit-cards';
import { CREDIT_CARDS_REWARD_TYPES, CREDIT_CARDS_REWARD_TYPE_DESCRIPTIONS } from '../constants';
import { FORMATS, VALUE_SIZES } from '../constants/drivers';
/**
 * Get the annual fee driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverAnnualFee = function getDriverAnnualFee(offer) {
  var annualFeeDetails = getAnnualFeeDetails(offer);
  return {
    id: 'annualFee',
    label: 'Annual fee',
    value: annualFeeDetails,
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/**
 * Get the balance transfer fee description driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverBalanceTransferFeeDescription = function getDriverBalanceTransferFeeDescription(offer) {
  var defaults = {
    id: 'balanceTransferFeeDescription',
    label: 'Balance transfer fee',
    valueSize: VALUE_SIZES.MEDIUM
  }; // Show the balance_transfer_fee_description if it exists

  var balanceTransferFeeDescription = getBalanceTransferFeeDescription(offer);

  if (!_isNil(balanceTransferFeeDescription)) {
    return _objectSpread(_objectSpread({}, defaults), {}, {
      value: balanceTransferFeeDescription
    });
  } // Fallback to show the regular balance_transfer_fee as a percentage


  var balanceTransferFee = getBalanceTransferFee(offer);
  return _objectSpread(_objectSpread({}, defaults), {}, {
    value: balanceTransferFee,
    valueFormat: FORMATS.PERCENT
  });
};
/**
 * Get the bonus driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverBonus = function getDriverBonus(offer) {
  var signUpBonus = getSignUpBonus(offer); // Calculate only if the bonus type is in points or miles else display the signupBonus directly

  var bonusValue = getSignUpBonusType(offer) !== CREDIT_CARDS_REWARD_TYPES.CASH ? signUpBonus * getRewardCentsPerUnitValue(offer) / 100 : signUpBonus; // Append the disclaimer text to the tool tip text

  var toolTipText = signUpBonus ? "".concat(getSignUpBonusDetails(offer), ". Our bonus value calculation may be different than our partner\u2019s.") : null;
  return {
    id: 'bonusValue',
    label: 'Est. bonus value',
    value: bonusValue || 0,
    valueFormat: FORMATS.CURRENCY,
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: toolTipText
  };
};
/**
 * Get the intro APR driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverIntroApr = function getDriverIntroApr(offer) {
  var introAprMessageList = getAPRIntroMessageList(offer);
  return {
    id: 'introApr',
    label: 'Intro APR',
    value: [].concat(introAprMessageList)[0],
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/**
 * Get the intro offer driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverIntroOffer = function getDriverIntroOffer(offer) {
  var introOfferValueFormat; // For Discover cards - the value is "Cashback Match" (a string)

  if (isDiscover(offer)) {
    introOfferValueFormat = FORMATS.STRING;
  } else {
    switch (getSignUpBonusType(offer)) {
      case CREDIT_CARDS_REWARD_TYPES.CASH:
        introOfferValueFormat = FORMATS.CURRENCY;
        break;

      case CREDIT_CARDS_REWARD_TYPES.MILES:
        introOfferValueFormat = FORMATS.NUMBER;
        break;

      case CREDIT_CARDS_REWARD_TYPES.POINTS:
        introOfferValueFormat = FORMATS.NUMBER;
        break;

      default:
        introOfferValueFormat = FORMATS.UNFORMATTED;
    }
  }

  return {
    id: 'introOffer',
    label: 'Intro offer',
    value: getIntroOfferValue(offer),
    valueFormat: introOfferValueFormat,
    valueSize: VALUE_SIZES.MEDIUM,
    description: CREDIT_CARDS_REWARD_TYPE_DESCRIPTIONS[getSignUpBonusType(offer)] || null,
    valueTooltip: getIntroOfferValueTooltip(offer)
  };
};
/**
 * Get the minimum deposit description driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverMinimumDepositDescription = function getDriverMinimumDepositDescription(offer) {
  // Props common to either field's display
  var defaults = {
    id: 'minimumDepositDescription',
    label: 'Minimum deposit',
    valueSize: VALUE_SIZES.MEDIUM
  }; // 1) Show the minimum_deposit_description if it exists

  var minimumDepositDescription = getMinimumDepositDescription(offer);

  if (!_isNil(minimumDepositDescription)) {
    return _objectSpread(_objectSpread({}, defaults), {}, {
      value: minimumDepositDescription
    });
  } // 2) If no minimum_deposit_description, show the regular minimum_deposit as a currency


  var minimumDeposit = getMinimumDeposit(offer);
  return _objectSpread(_objectSpread({}, defaults), {}, {
    value: minimumDeposit,
    valueFormat: FORMATS.CURRENCY
  });
};
/**
 * Get the recommended credit score range quick fact
 * Used for in the Match Score AB test to replace the `getQuickFactLikelihoodOfApproval`.
 * @see {@link https://nerdwallet.atlassian.net/browse/SHOP-8615}
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverRecommendedCreditScoreRange = function getDriverRecommendedCreditScoreRange(offer) {
  var minCreditScoreIndex = getMinimumFICO(offer);
  var maxCreditScoreIndex = getMaximumFICO(offer);

  if (!(minCreditScoreIndex && maxCreditScoreIndex)) {
    return null;
  }

  var _getRecommendedCredit = getRecommendedCreditScoreFormattedDetails(offer),
      creditScoreRange = _getRecommendedCredit.creditScoreRange,
      creditScoreRangeDescription = _getRecommendedCredit.creditScoreRangeDescription;

  return {
    id: 'recommendedCreditScoreRange',
    label: 'Rec credit score',
    value: !_isEmpty(offer) ? "".concat(creditScoreRange) : null,
    valueSize: VALUE_SIZES.MEDIUM,
    description: creditScoreRangeDescription
  };
};
/**
 * Get the reward rate driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverRewardRate = function getDriverRewardRate(offer) {
  return {
    id: 'rewardRate',
    label: 'Rewards rate',
    value: getRewardRateDetails(offer),
    valueSize: VALUE_SIZES.MEDIUM,
    description: getRewardRateDescription(offer),
    valueTooltip: getRewardRateNotes(offer)
  };
};
/**
 * @todo - getDriverLikelihoodOfApproval
 * Needs structured content support.
 * @see {@link https://github.com/NerdWallet/react-components/blob/b1476dbf238b24ee9820d0ead74c6a2e257d823a/components/quick-facts/src/mappings/credit-cards.jsx#L132-L179}
 */

/**
 * @todo - getDriverLikelihoodOfApprovalText
 * Needs structured content support.
 * @see {@link https://github.com/NerdWallet/react-components/blob/b1476dbf238b24ee9820d0ead74c6a2e257d823a/components/quick-facts/src/mappings/credit-cards.jsx#L239-L338}
 */

/**
 * Get the regular apr driver
 * @param {Object} offer - The marketplace entity
 */

export var getDriverRegularApr = function getDriverRegularApr(offer) {
  return {
    id: 'regularApr',
    label: 'Regular APR',
    value: getOngoingAPR(offer),
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/**
 * @todo - getDriverStarRating
 * Needs structured content support.
 * @see {@link https://github.com/NerdWallet/react-components/blob/b1476dbf238b24ee9820d0ead74c6a2e257d823a/components/quick-facts/src/mappings/credit-cards.jsx#L569-L606}
 */